import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Countdown } from "@/components/Countdown";
import { Brush } from "@/components/brand/Lettering";
import { monthLabel, shareImg, shareLink } from "@/lib/api";
import { useShareReward } from "@/context/AuthContext";

const COLORS = ["#D7FF5B", "#FF7EC7", "#7FD3FF", "#111111"];

export const Confetti = ({ count = 48 }) => {
  const bits = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left: Math.random() * 100, delay: Math.random() * 2.5, dur: 3.2 + Math.random() * 2.5, rot: Math.random() * 360,
    w: 6 + Math.random() * 8, h: 10 + Math.random() * 12, c: COLORS[i % COLORS.length],
  })), [count]);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="confetti">
      {bits.map((b, i) => (
        <span key={i} className="confetti-bit" style={{ left: `${b.left}%`, width: b.w, height: b.h, background: b.c, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s`, transform: `rotate(${b.rot}deg)` }} />
      ))}
    </div>
  );
};

export const VotingCountdown = ({ closesAt, testid = "sotm-countdown" }) => (
  <div data-testid={testid}>
    <p className="label font-bold">Voting closes in</p>
    <div className="mt-3"><Countdown size="sm" target={closesAt} testid={`${testid}-timer`} /></div>
  </div>
);

export const Leaderboard = ({ rows, testid = "sotm-leaderboard" }) => (
  <ol className="mt-8 grid gap-4 sm:grid-cols-3" data-testid={testid}>
    {rows.map((s, i) => (
      <li key={s.id} className={`flex items-center gap-4 rounded-[16px] border-[1.5px] border-ink p-3 ${["bg-lime", "bg-pink", "bg-sky"][i]}`} data-testid={`${testid}-${i + 1}`}>
        <span className="display text-5xl">{i + 1}</span>
        <img src={s.design_url} alt="" className="h-16 w-16 rounded-[12px] border border-ink bg-cloud object-cover" />
        <div className="min-w-0"><p className="truncate text-[15px] font-medium">{s.design_title}</p><p className="label !text-[10px]">{s.first_name} · {s.votes} votes</p></div>
      </li>
    ))}
  </ol>
);

export const ClosedBanner = ({ rows, testid = "sotm-closed" }) => (
  <div className="rounded-[24px] border-[1.5px] border-ink bg-cloud p-6 sm:p-8" data-testid={testid}>
    <p className="display text-[clamp(2.25rem,4vw,3.5rem)]">Votes are in.</p>
    <p className="label mt-2 font-bold">Winner announced soon · Top 3</p>
    <Leaderboard rows={rows} testid={`${testid}-leaderboard`} />
  </div>
);

export const WinnerReveal = ({ w, testid = "sotm-winner-reveal", compact }) => (
  <section className="relative overflow-hidden rounded-[24px] border-[1.5px] border-ink bg-lime" data-testid={testid}>
    <Confetti />
    <div className={`relative grid items-center gap-8 lg:grid-cols-12 ${compact ? "p-6 sm:p-8" : "p-6 sm:p-12"}`}>
      <div className="lg:col-span-6">
        <span className="sticker bg-pink !text-xs">Winner announced · {monthLabel(w.month)}</span>
        <Brush lines={["Winner", "announced"]} halo={false} className="mt-6 text-[clamp(3rem,7vw,6rem)]" />
        <p className="display mt-6 text-[clamp(2rem,4vw,3.25rem)]" data-testid={`${testid}-title`}>{w.design_title}</p>
        <p className="mt-2 text-lg">by {w.first_name}, {w.age} · {w.city}</p>
        <div className="mt-8 flex flex-wrap items-center gap-5">
          <Link to={`/sus-of-the-month/${w.slug}`} className="btn btn-ink" data-testid={`${testid}-feature`}>See the feature <ArrowRight size={16} /></Link>
          {w.product_slug && <Link to={`/product/${w.product_slug}`} className="link-u" data-testid={`${testid}-tee`}>Shop the tee <ArrowRight size={14} /></Link>}
        </div>
      </div>
      <div className="lg:col-span-5 lg:col-start-8">
        <div className="aspect-square -rotate-2 overflow-hidden rounded-[24px] border-[1.5px] border-ink bg-cloud shadow-[8px_8px_0_#111]"><img src={w.design_url} alt={`${w.design_title} by ${w.first_name}`} className="h-full w-full object-cover mix-blend-multiply" /></div>
      </div>
    </div>
  </section>
);

export const ShareMenu = ({ s, testid }) => {
  const reward = useShareReward();
  const share = async () => {
    const url = shareLink(s);
    const text = `Vote for "${s.design_title}" by ${s.first_name} in Sus of the Month at the suspects 👀`;
    if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
      try { await navigator.share({ title: s.design_title, text, url }); reward("sotm", s.id); return; } catch (e) { if (e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Vote link copied. Paste it anywhere."); reward("sotm", s.id); } catch { toast(url); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={testid}>
      <button onClick={share} className="label inline-flex items-center gap-1.5 font-bold hover:text-ink/60" data-testid={`${testid}-share`}><Share2 size={13} /> Share</button>
      <span className="label !text-[10px] text-ink/55">Get your story image:</span>
      <a href={shareImg(s.id, "story", true)} download className="label inline-flex items-center gap-1 font-bold underline-offset-4 hover:underline" data-testid={`${testid}-story`}><Download size={12} /> Story</a>
      <a href={shareImg(s.id, "square", true)} download className="label inline-flex items-center gap-1 font-bold underline-offset-4 hover:underline" data-testid={`${testid}-square`}><Download size={12} /> Square</a>
    </div>
  );
};
